import { createClient } from '@supabase/supabase-js';
import { UserProfile } from './userProfile';
import { Point, Cardinalidad } from '../types';
import { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { formatDateColombia } from '../utils/dateFormat';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const initialCredits = import.meta.env.VITE_INITIAL_CREDITS ? parseInt(import.meta.env.VITE_INITIAL_CREDITS) : 1000;
const enableOtp = import.meta.env.VITE_ENABLE_OTP === 'true';

/** URL base de la app para enlaces en correos (verificación, etc.). En producción usar VITE_APP_URL o el origin actual. */
export function getAppBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return import.meta.env.VITE_APP_URL ?? 'https://topografo.codytion.com';
}

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Cache para almacenar el último valor de créditos
let creditsCache: { [key: string]: { credits: number; timestamp: number } } = {};
const CACHE_DURATION = 30000; // 30 segundos

// Cache para perfiles
let profileCache: { [key: string]: { data: any; timestamp: number } } = {};
const PROFILE_CACHE_DURATION = 60000; // 1 minuto

// Cache para la sesión
let sessionCache: { session: any; timestamp: number } | null = null;
const SESSION_CACHE_DURATION = 300000; // 5 minutos

// Crear una única instancia del cliente
let supabaseInstance: ReturnType<typeof createClient> | null = null;

// Función para limpiar todos los datos de localStorage relacionados con la sesión
export const clearSessionData = async () => {
  // Obtener userId antes de cerrar sesión para limpiar solo sus datos
  let userId: string | null = null;
  try {
    const { data: { user } } = await supabase().auth.getUser();
    userId = user?.id ?? null;
  } catch {
    /* ignore */
  }

  // Limpiar localStorage
  localStorage.removeItem('otpValidated');
  localStorage.removeItem('showArea');
  localStorage.removeItem('showPerimeter');
  localStorage.removeItem('showEditInstructions');

  // Limpiar datos del documento solo del usuario actual (evita borrar datos de otros usuarios)
  if (userId) {
    const { clearUserDocumentData } = await import('../utils/documentStorage');
    await clearUserDocumentData(userId);
  } else {
    // Sin userId: limpiar claves legacy por compatibilidad
    localStorage.removeItem('documentInfo');
    localStorage.removeItem('signatureImage');
    localStorage.removeItem('logoImage');
  }

  // Limpiar claves de OTP
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('otpGeneratedFor:')) {
      localStorage.removeItem(key);
    }
  });
  // Limpiar sessionStorage (tutorial, etc.)
  try {
    sessionStorage.clear();
  } catch {
    /* Ignorar si no está disponible */
  }
  // Limpiar caches
  creditsCache = {};
  profileCache = {};
  sessionCache = null;
};

export const supabase = () => {
  if (!supabaseInstance) {
    try {
      supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          storage: window.localStorage,
          flowType: 'pkce',
          debug: false
        }
      });

      // Configurar el listener de cambios de autenticación
      supabaseInstance.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
        if (event === 'TOKEN_REFRESHED') {
          console.log('Token refrescado exitosamente');
        } else if (event === 'SIGNED_OUT') {
          console.log('Sesión cerrada');
          void clearSessionData();
        }
      });
    } catch (error) {
      console.error('Error al inicializar Supabase:', error);
      throw error;
    }
  }
  return supabaseInstance;
};

// Exportar la instancia por defecto para compatibilidad
export default supabase();

// Authentication functions
export async function signIn(email: string, password: string) {
  try {
    console.log('Intentando iniciar sesión con email:', email);

    // Primero verificar si el email existe en la base de datos
    const normalizedEmail = email.toLowerCase();
    const { data: profileCheck, error: profileCheckError } = await supabase()
      .from('profiles')
      .select('email_verified, email, id')
      .eq('email', normalizedEmail)
      .maybeSingle();

    console.log('Resultado de la consulta de perfil:', { profileCheck, profileCheckError });

    // Si no existe el perfil o hay un error, no permitir el inicio de sesión
    if (profileCheckError) {
      console.error('Error al verificar perfil:', profileCheckError);
      return { 
        data: null, 
        error: 'Error al verificar el estado de la cuenta.' 
      };
    }

    if (!profileCheck) {
      console.log('No se encontró perfil para el email:', email);
      return { 
        data: null, 
        error: 'Tu cuenta aún no está activada. Por favor, revisa tu correo electrónico para activarla.' 
      };
    }

    console.log('Perfil encontrado:', profileCheck);

    // Verificar el estado de verificación del email
    if (!profileCheck.email_verified) {
      console.log('Email no verificado para:', email);
      
      // Intentar verificar si el usuario ya confirmó su email en auth.users
      const { data: authUser, error: authError } = await supabase()
        .from('auth.users')
        .select('email_confirmed_at')
        .eq('id', profileCheck.id as string)
        .single() as { data: { email_confirmed_at: string | null } | null, error: any };

      if (!authError && authUser?.email_confirmed_at) {
        // Si el email está confirmado en auth.users pero no en profiles, actualizar profiles
        const { error: updateError } = await supabase()
          .from('profiles')
          .update({ email_verified: true })
          .eq('id', profileCheck.id as string);

        if (!updateError) {
          console.log('Perfil actualizado con email_verified = true');
        }
      } else {
        return {
          data: null,
          error: 'Por favor, verifica tu correo electrónico antes de iniciar sesión.'
        };
      }
    }

    // Si el email está verificado, proceder con el inicio de sesión
    const { data, error } = await supabase().auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      let errorMessage = 'Error al iniciar sesión';
      
      // Manejar diferentes tipos de errores de Supabase
      switch (error.message) {
        case 'Invalid login credentials':
          errorMessage = 'Clave incorrecta.';
          break;
        case 'Email not confirmed':
          errorMessage = 'Por favor, confirma tu correo electrónico antes de iniciar sesión.';
          break;
        case 'Too many requests':
          errorMessage = 'Demasiados intentos. Por favor, espera unos minutos antes de intentar nuevamente.';
          break;
        default:
          errorMessage = error.message;
          break;
      }

      if (error.message !== 'Invalid login credentials') {
        console.error('Error en inicio de sesión:', errorMessage);
      }
      return { data: null, error: errorMessage };
    }

    if (!data.user) {
      console.error('No se pudo obtener el usuario después del inicio de sesión');
      return { 
        data: null, 
        error: 'No se pudo iniciar sesión. Por favor, intenta nuevamente.' 
      };
    }

    console.log('Usuario autenticado:', data.user.id);

    // Verificar el perfil del usuario (sin exigir OTP en el login)
    const { error: profileError } = await supabase()
      .from('profiles')
      .select('role')
      .eq('id', data.user.id)
      .single();

    if (profileError) {
      console.error('Error al obtener perfil después de autenticación:', profileError);
      await supabase().auth.signOut();
      return {
        data: null,
        error: 'Error al cargar el perfil de usuario.'
      };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error inesperado en signIn:', error);
    return { 
      data: null, 
      error: 'Error inesperado al iniciar sesión.' 
    };
  }
}

/** Solicitar recuperación de contraseña. Edge Function genera el link correcto y n8n envía el correo. */
export async function resetPasswordForEmail(email: string): Promise<{ error: string | null }> {
  try {
    const redirectTo = `${getAppBaseUrl()}/reset-password`;
    const fnUrl = `${supabaseUrl}/functions/v1/request-password-reset`;
    const response = await fetch(fnUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ email: email.toLowerCase().trim(), redirectTo }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { error: data?.error || `Error ${response.status}` };
    }
    return { error: null };
  } catch (err) {
    console.error('Error inesperado en resetPasswordForEmail:', err);
    return { error: err instanceof Error ? err.message : 'Error al solicitar recuperación.' };
  }
}

/** Actualizar contraseña (cuando el usuario llega desde el enlace de recuperación). */
export async function updatePassword(newPassword: string): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase().auth.updateUser({ password: newPassword });
    if (error) {
      console.error('Error al actualizar contraseña:', error);
      return { error: error.message };
    }
    return { error: null };
  } catch (err) {
    console.error('Error inesperado en updatePassword:', err);
    return { error: err instanceof Error ? err.message : 'Error al actualizar contraseña.' };
  }
}

// Funciones de validación
function validateEmail(email: string): string | null {
  if (!email || !email.includes('@')) {
    return 'El correo electrónico no es válido';
  }
  return null;
}

function validatePhone(phone: string): string | null {
  if (!phone || phone.length < 8) {
    return 'El número de teléfono debe tener al menos 8 dígitos';
  }
  return null;
}

function validateName(firstName: string, lastName: string): string | null {
  if (!firstName || firstName.length < 2) {
    return 'El nombre debe tener al menos 2 caracteres';
  }
  if (!lastName || lastName.length < 2) {
    return 'El apellido debe tener al menos 2 caracteres';
  }
  return null;
}

export const checkEmailExists = async (email: string) => {
  try {
    const { count, error } = await supabase()
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('email', email);

    if (error) {
      console.error('Error al verificar email:', error);
      throw error;
    }

    return count ? count > 0 : false;
  } catch (error) {
    console.error('Error al verificar email:', error);
    if (error instanceof Error) throw error;
    throw new Error(String(error));
  }
};

export const signUp = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  phone: string
) => {
  try {
    // Validaciones
    const emailError = validateEmail(email);
    if (emailError) return { error: emailError };

    const nameError = validateName(firstName, lastName);
    if (nameError) return { error: nameError };

    const phoneError = validatePhone(phone);
    if (phoneError) return { error: phoneError };

    // Crear el usuario en Auth usando el cliente de Supabase
    const { data: authData, error: signUpError } = await supabase().auth.signUp({
      email,
      password,
      options: {
        data: {
          email,
          first_name: firstName,
          last_name: lastName,
          phone: phone,
          email_verified: false,
          phone_verified: false
        }
      },
    });

    if (signUpError) {
      console.error('Error al crear usuario:', signUpError);
      const msg = signUpError.message?.toLowerCase() ?? '';
      if (msg.includes('database error saving new user') || msg.includes('database error')) {
        return { error: 'Hay un problema temporal en el servidor y no se pudo crear tu cuenta. Por favor, inténtalo más tarde o contacta a soporte.' };
      }
      return { error: 'Error al crear el usuario. Por favor, intenta con otro correo electrónico.' };
    }

    if (!authData.user) {
      return { error: 'No se pudo crear el usuario' };
    }

    // Enviar correo de verificación. El webhook debe usar verifyEmailBaseUrl para el enlace, ej: {{verifyEmailBaseUrl}}/verify-email/{{userId}}
    try {
      const response = await fetch('https://codytion.com/app-n8n/webhook/email-validation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          userId: authData.user.id,
          verifyEmailBaseUrl: getAppBaseUrl(),
        })
      });

      if (!response.ok) {
        console.error('Error al enviar correo de verificación:', await response.text());
        return { error: 'Error al enviar el correo de verificación' };
      }

      const data = await response.json();
      const messageId = data?.SendEmailResponse?.SendEmailResult?.MessageId;
      if (!messageId) {
        console.error('Respuesta inesperada del endpoint de email-validation:', data);
        return { error: 'No se pudo enviar el correo de verificación. Intenta nuevamente.' };
      }

      // Forzar cierre de sesión después del registro
      await supabase().auth.signOut();

      return {
        message: 'Usuario registrado exitosamente. Por favor, verifica tu correo electrónico para activar tu cuenta.',
        userId: authData.user.id
      };
    } catch (emailError) {
      console.error('Error al enviar correo de verificación:', emailError);
      return { error: 'Error al enviar el correo de verificación' };
    }
  } catch (error) {
    console.error('Error en signUp:', error);
    return {
      error: error instanceof Error ? error.message : 'Error al registrar usuario',
    };
  }
};

export async function signOut() {
  try {
    await clearSessionData();
    
    const { error } = await supabase().auth.signOut();
    if (error) throw error;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Auth session missing')) {
      // Limpiar localStorage y caches incluso si no hay sesión
      localStorage.clear();
      creditsCache = {};
      profileCache = {};
      sessionCache = null;
      return;
    }
    throw error;
  }
}

export async function saveMap(coordinates: Point[], filename: string = 'Nuevo mapa') {
  try {
    const { data: { user }, error: userError } = await supabase().auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('No user authenticated');

    const { data, error } = await supabase()
      .from('maps')
      .insert({
        coordinates,
        name: filename,
        user_id: user.id
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned from insert operation');
    
    return { id: String(data.id), ...data };
  } catch (error) {
    console.error('Error saving map:', error);
    if (error instanceof Error) throw error;
    throw new Error(String(error));
  }
}

function boundaryRowPayload(mapId: string, boundary: any) {
  return {
    map_id: mapId,
    points: boundary.coordinates || boundary.points,
    start_point: 0,
    end_point: 0,
    colindante_id: boundary.colindante_id,
    color: boundary.color,
    cardinalidad: boundary.cardinalidad || 'Norte',
    text_angle: boundary.labelAngle || 0,
    text_position: boundary.labelOffset
      ? {
          x: boundary.labelOffset.dx || 0,
          y: boundary.labelOffset.dy || 0,
        }
      : { x: 0, y: 0 },
  };
}

export async function saveBoundaries(mapId: string, boundaries: any[]) {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase().auth.getUser();
    if (userError) throw new Error(userError.message);
    if (!user) throw new Error('No user authenticated');

    const results: any[] = [];

    for (const boundary of boundaries) {
      const payload = boundaryRowPayload(mapId, boundary);

      if (boundary.id) {
        const { data, error } = await supabase()
          .from('boundaries')
          .update(payload)
          .eq('id', boundary.id)
          .eq('map_id', mapId)
          .select()
          .single();

        if (error) throw new Error(error.message);
        if (!data) throw new Error('No data returned from update operation');
        results.push(data);
      } else {
        const insertPayload = {
          ...payload,
          created_at: boundary.created_at || new Date().toISOString(),
          created_by: user.id,
        };

        const { data, error } = await supabase()
          .from('boundaries')
          .insert(insertPayload)
          .select()
          .single();

        if (error) throw new Error(error.message);
        if (!data) throw new Error('No data returned from insert operation');
        results.push(data);
      }
    }

    return results;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error('Error saving boundaries');
  }
}

export async function saveDocumentCustomization(mapId: string, customization: any) {
  try {
    const { data: { user }, error: userError } = await supabase().auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('No user authenticated');

    const documentData = {
      map_id: mapId,
      company_name: customization.companyName || 'Default Company',
      signature_image: customization.signatureImage
    };

    const { error } = await supabase()
      .from('document_customizations')
      .insert(documentData);

    if (error) throw error;
  } catch (error) {
    console.error('Error saving document customization:', error);
    throw error;
  }
}

interface ProfileData {
  credits: number;
  credits_promotional?: number;
  credits_real?: number;
  [key: string]: any;
}

// Función para obtener el perfil con caché
export async function getProfile(userId: string, select: string = '*') {
  const cacheKey = `${userId}-${select}`;
  const now = Date.now();
  
  // Verificar si tenemos un valor en caché válido
  if (profileCache[cacheKey] && (now - profileCache[cacheKey].timestamp) < PROFILE_CACHE_DURATION) {
    return profileCache[cacheKey].data;
  }

  const { data, error } = await supabase()
    .from('profiles')
    .select(select)
    .eq('id', userId)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  // Actualizar el caché
  profileCache[cacheKey] = {
    data,
    timestamp: now
  };

  return data;
}

// Función para obtener la sesión con caché
export async function getSession() {
  const now = Date.now();
  
  if (sessionCache && (now - sessionCache.timestamp) < SESSION_CACHE_DURATION) {
    return sessionCache.session;
  }

  const { data: { session }, error } = await supabase().auth.getSession();
  
  if (error) {
    console.error('Error al obtener sesión:', error);
    throw error;
  }

  sessionCache = {
    session,
    timestamp: now
  };

  return session;
}

// Función para invalidar el caché de perfiles
export function invalidateProfileCache(userId?: string) {
  if (userId) {
    // Eliminar todas las entradas de caché para este usuario
    Object.keys(profileCache).forEach(key => {
      if (key.startsWith(`${userId}-`)) {
        delete profileCache[key];
      }
    });
  } else {
    profileCache = {};
  }
}

// Función para invalidar el caché de sesión
export function invalidateSessionCache() {
  sessionCache = null;
}

// Modificar getUserCredits para usar el nuevo sistema de caché
export async function getUserCredits(): Promise<number> {
  try {
    const session = await getSession();
    if (!session?.user) return 0;

    const profile = await getProfile(session.user.id, 'credits');
    return profile?.credits ?? 0;
  } catch (error) {
    console.error('Error al obtener créditos:', error);
    return 0;
  }
}

// Función para invalidar el caché de créditos
export function invalidateCreditsCache(userId?: string) {
  if (userId) {
    delete creditsCache[userId];
  } else {
    creditsCache = {};
  }
}

// Función optimizada que registra transacciones y consume créditos
export async function consumeCredits(
  amount: number, 
  options?: {
    mapId?: string;
    pointsCount?: number;
    description?: string;
    documentFilename?: string;
  }
): Promise<number> {
  try {
    const { data: { user }, error: userError } = await supabase().auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Usuario no autenticado');

    // Obtener créditos actuales desde profiles
    const { data: profileData, error: profileError } = await supabase()
      .from('profiles')
      .select('credits, credits_promotional, credits_real')
      .eq('id', user.id)
      .single();

    if (profileError) throw new Error('Error al verificar créditos');
    if (!profileData) throw new Error('No se encontró el perfil del usuario');

    const currentCredits = (profileData as ProfileData).credits;
    const currentPromotional = Number((profileData as ProfileData).credits_promotional) || 0;
    const currentReal = Number((profileData as ProfileData).credits_real) || 0;
    if (currentCredits < amount) {
      throw new Error('Créditos insuficientes');
    }

    // Política contable: promo_first
    let remainingToConsume = amount;
    const consumedPromotional = Math.min(currentPromotional, remainingToConsume);
    remainingToConsume -= consumedPromotional;
    const consumedReal = Math.min(currentReal, remainingToConsume);
    remainingToConsume -= consumedReal;
    if (remainingToConsume > 0) {
      throw new Error('Créditos insuficientes para aplicar consumo por bolsas');
    }

    const newPromotional = currentPromotional - consumedPromotional;
    const newReal = currentReal - consumedReal;
    const newCredits = newPromotional + newReal;
    
    // Actualizar créditos en profiles
    const { error: updateError } = await supabase()
      .from('profiles')
      .update({
        credits: newCredits,
        credits_promotional: newPromotional,
        credits_real: newReal,
      })
      .eq('id', user.id);

    if (updateError) throw new Error(`Error al actualizar créditos: ${updateError.message}`);

    // Registrar la transacción en credit_transactions (obligatorio: si falla todo, revertimos saldo)
    const description = options?.description || 'Consumo de créditos';
    const pointsCount = options?.pointsCount || 0;

    const fullRow: Record<string, unknown> = {
      user_id: user.id,
      amount: -amount,
      description,
      points_count: pointsCount,
      is_read: false,
      consumed_promotional: consumedPromotional,
      consumed_real: consumedReal,
    };
    if (options?.mapId) {
      fullRow.map_id = options.mapId;
    }
    if (options?.documentFilename) {
      fullRow.document_filename = options.documentFilename;
    }

    const insertAttempts: Record<string, unknown>[] = [];
    insertAttempts.push({ ...fullRow });

    const withoutFilename = { ...fullRow };
    delete withoutFilename.document_filename;
    if (JSON.stringify(withoutFilename) !== JSON.stringify(insertAttempts[0])) {
      insertAttempts.push(withoutFilename);
    }

    const withoutMap = { ...withoutFilename };
    delete withoutMap.map_id;
    if (JSON.stringify(withoutMap) !== JSON.stringify(withoutFilename)) {
      insertAttempts.push(withoutMap);
    }

    insertAttempts.push({
      user_id: user.id,
      amount: -amount,
      description,
      points_count: pointsCount,
      is_read: false,
    });
    insertAttempts.push({
      user_id: user.id,
      amount: -amount,
      description,
      points_count: pointsCount,
    });

    const seenPayloads = new Set<string>();
    const uniqueAttempts = insertAttempts.filter((row) => {
      const key = JSON.stringify(row);
      if (seenPayloads.has(key)) return false;
      seenPayloads.add(key);
      return true;
    });

    let lastInsertError: { message?: string; code?: string } | null = null;
    let inserted = false;

    for (let i = 0; i < uniqueAttempts.length; i++) {
      const row = Object.fromEntries(
        Object.entries(uniqueAttempts[i]).filter(([, v]) => v !== undefined)
      );
      const { error: insErr } = await supabase().from('credit_transactions').insert(row);
      if (!insErr) {
        inserted = true;
        break;
      }
      lastInsertError = insErr;
      console.warn(
        `[consumeCredits] Insert en credit_transactions intento ${i + 1}/${uniqueAttempts.length}:`,
        insErr.message || insErr
      );
    }

    if (!inserted) {
      const { error: revertErr } = await supabase()
        .from('profiles')
        .update({ credits: currentCredits })
        .eq('id', user.id);
      if (revertErr) {
        console.error('[consumeCredits] CRÍTICO: no se pudo revertir créditos tras fallo de insert:', revertErr);
      }
      invalidateCreditsCache(user.id);
      throw new Error(
        `No se pudo registrar el consumo en el historial (${lastInsertError?.message || 'error desconocido'}). ` +
          'El saldo no se ha modificado. Revisa FK de map_id, columnas (document_filename, is_read) y políticas RLS en Supabase.'
      );
    }

    // Actualizar el caché con los nuevos créditos
    creditsCache[user.id] = {
      credits: newCredits,
      timestamp: Date.now()
    };

    return newCredits;
  } catch (error) {
    throw error instanceof Error ? error : new Error('Error desconocido al consumir créditos');
  }
}

export const updateProfile = async (updates: Partial<UserProfile>) => {
  try {
    const { data: { user }, error: userError } = await supabase().auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('No user authenticated');
    const { data, error } = await supabase()
      .from('profiles')
      .update({
        first_name: updates.first_name,
        last_name: updates.last_name,
        full_name: `${updates.first_name} ${updates.last_name}`.trim(),
        phone: updates.phone,
        professional_card: updates.professional_card,
        company_name: updates.company_name,
        company_email: updates.company_email,
        company_phone: updates.company_phone,
        logo_image: updates.logo_image,
        signature_image: updates.signature_image,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(String(error));
  }
};

const N8N_WEBHOOK_BASE = 'https://codytion.com/app-n8n/webhook';

/** Datos de auditoría para el correo de eliminación de cuenta */
export interface DeletionAuditData {
  deletedBy: { name: string; email: string };
  deletedAt: string;
  userDeleted: { firstName: string; lastName: string; email: string; role: string; credits: number };
  auditLogs: Array<{
    field_changed: string;
    old_value: string;
    new_value: string;
    created_at: string;
    modifier_name: string;
  }>;
  auditHtml: string;
}

const FIELD_LABELS: Record<string, string> = {
  role: 'Rol',
  credits: 'Créditos',
  account_deleted: 'Estado de cuenta'
};

export function buildDeletionAuditHtml(data: Omit<DeletionAuditData, 'auditHtml'>): string {
  const rows = data.auditLogs.map(
    (log) =>
      `<tr><td style="padding:8px;border-bottom:1px solid #333;">${FIELD_LABELS[log.field_changed] ?? log.field_changed}</td>` +
      `<td style="padding:8px;border-bottom:1px solid #333;">${log.old_value || '-'}</td>` +
      `<td style="padding:8px;border-bottom:1px solid #333;">${log.new_value || '-'}</td>` +
      `<td style="padding:8px;border-bottom:1px solid #333;">${log.modifier_name}</td>` +
      `<td style="padding:8px;border-bottom:1px solid #333;">${formatDateColombia(log.created_at)}</td></tr>`
  ).join('');
  return (
    `<p style="color:#e0e0e0;font-size:14px;margin:15px 0 5px;"><strong>Eliminado por:</strong> ${data.deletedBy.name} (${data.deletedBy.email})</p>` +
    `<p style="color:#e0e0e0;font-size:14px;margin:5px 0 15px;"><strong>Fecha:</strong> ${formatDateColombia(data.deletedAt)}</p>` +
    `<p style="color:#e0e0e0;font-size:14px;margin:15px 0 5px;"><strong>Cuenta eliminada:</strong> ${data.userDeleted.firstName} ${data.userDeleted.lastName} (${data.userDeleted.email}) - Rol: ${data.userDeleted.role} - Créditos: ${data.userDeleted.credits}</p>` +
    (rows
      ? `<table style="width:100%;border-collapse:collapse;margin:15px 0;font-size:13px;"><thead><tr style="background:#2a2a2a;"><th style="padding:8px;text-align:left;">Campo</th><th style="padding:8px;text-align:left;">Valor anterior</th><th style="padding:8px;text-align:left;">Valor nuevo</th><th style="padding:8px;text-align:left;">Modificado por</th><th style="padding:8px;text-align:left;">Fecha</th></tr></thead><tbody>${rows}</tbody></table>`
      : '')
  );
}

export const deleteUser = async (
  userId: string,
  email?: string,
  auditData?: DeletionAuditData
) => {
  try {
    // Obtener el usuario autenticado para el registro de auditoría
    const { data: { user: currentUser }, error: userError } = await supabase().auth.getUser();
    if (userError || !currentUser) {
      throw new Error('No se pudo obtener el usuario autenticado');
    }

    // Ejecutar la eliminación en una sola transacción SQL
    const { error } = await supabase().rpc('handle_user_deletion', {
      p_user_id: userId,
      p_admin_id: currentUser.id
    });

    if (error) {
      console.error('Error en la eliminación del usuario:', error);
      throw error;
    }

    // Enviar correo de confirmación de eliminación con datos de auditoría
    if (email) {
      try {
        const response = await fetch(`${N8N_WEBHOOK_BASE}/delete-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            auditData: auditData ?? null
          })
        });
        if (!response.ok) {
          console.warn('No se pudo enviar el correo de eliminación:', await response.text());
        }
      } catch (emailErr) {
        console.warn('Error al enviar correo de eliminación:', emailErr);
      }
    }

    // Si llegamos aquí, la eliminación fue exitosa
    console.log('Usuario eliminado exitosamente:', {
      userId,
      deletedBy: currentUser.id,
      timestamp: new Date().toISOString()
    });

    return {
      success: true,
      message: 'Usuario eliminado correctamente'
    };
  } catch (error) {
    console.error('Error detallado en deleteUser:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al eliminar usuario'
    };
  }
};

export const deleteBoundary = async (mapId: string, boundaryId: string) => {
  const { error } = await supabase()
    .from('boundaries')
    .delete()
    .eq('map_id', mapId)
    .eq('id', boundaryId);

  return { error };
};

export const requestEmailValidation = async (email: string, userId?: string) => {
  try {
    let finalUserId: string | undefined = userId;
    if (!finalUserId) {
      // Verificar si el email existe en la base de datos
      const { data: userData, error: userError } = await supabase()
        .from('profiles')
        .select('id')
        .eq('email', email)
        .single();

      if (userError || !userData) {
        return { error: 'No se encontró una cuenta asociada a este correo electrónico' };
      }
      finalUserId = String(userData.id);
    }

    // Enviar correo de verificación usando el endpoint personalizado
    const response = await fetch('https://codytion.com/app-n8n/webhook/email-validation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        userId: finalUserId,
        verifyEmailBaseUrl: getAppBaseUrl(),
      })
    });

    if (!response.ok) {
      console.error('Error al enviar correo de verificación:', await response.text());
      return { error: 'Error al enviar el correo de verificación' };
    }

    return { 
      success: true,
      message: 'Se ha enviado un correo de verificación a tu dirección de correo electrónico.'
    };
  } catch (error) {
    console.error('Error en requestEmailValidation:', error);
    return {
      error: error instanceof Error ? error.message : 'Error al procesar la solicitud de verificación',
    };
  }
};

// Función para verificar si OTP está habilitado
export function isOtpEnabled(): boolean {
  return enableOtp;
}